/**
 * ============================================================
 * ⚠️  PROTECTED FILE - DO NOT MODIFY WITHOUT APPROVAL  ⚠️
 * ============================================================
 * This is the Sales Dashboard shown at /sales
 * Contains: Good Morning greeting, Action Items, Today's Diary,
 * Performance stats with My Stats/Office tabs, Pipeline Value,
 * Recent Instructions, Sales Agreed, Exchanges, Completions
 * 
 * Layout and functionality have been approved by the client.
 * Any changes require explicit approval.
 * ============================================================
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Home, PoundSterling, Users, Clock, CheckCircle,
  Plus, Search, TrendingUp, Loader2, Calendar, Eye,
  Phone, Mail, ChevronRight, RefreshCw, ClipboardList,
  UserCheck, Target, Building, MessageSquare, Star,
  AlertCircle, CheckSquare, ArrowUpRight, Briefcase,
  CalendarDays, FileText, UserPlus, X, MapPin
} from "lucide-react";
import { API } from "@/App";
import { AddInstructionModal } from "@/components/instructions/AddInstructionModal";
import { AddApplicantForm } from "@/components/applicants/AddApplicantForm";

const priorityColors = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-blue-100 text-blue-700 border-blue-200",
  low: "bg-slate-100 text-slate-600 border-slate-200"
};

const priorityIcons = {
  urgent: AlertCircle,
  high: Star,
  medium: Clock,
  low: CheckSquare
};

export const SalesDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  // SI-P0-04: Removed showNewInstruction - now redirects to /sales/instructions/list
  const [showAddApplicant, setShowAddApplicant] = useState(false);
  const [showAddTodo, setShowAddTodo] = useState(false);
  const [showSetTargets, setShowSetTargets] = useState(false);
  const [diaryTab, setDiaryTab] = useState("personal");
  const [newTodo, setNewTodo] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium"
  });
  const [targets, setTargets] = useState({
    instructions: 0,
    sales_agreed: 0,
    exchanges: 0,
    completions: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sales/dashboard/personal`, { headers });
      setDashboardData(res.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Global applicant search
  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    
    setSearching(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/dashboard/search?q=${encodeURIComponent(searchQuery)}`, { headers });
      setSearchResults(res.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, token]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, handleSearch]);

  // Complete a to-do
  const handleCompleteTodo = async (todoId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/sales/dashboard/todo/${todoId}/complete`, {}, { headers });
      toast.success("Task completed!");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  // Add new to-do
  const handleAddTodo = async () => {
    if (!newTodo.title) {
      toast.error("Please enter a task title");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/sales/dashboard/todo`, newTodo, { headers });
      toast.success("Task added!");
      setShowAddTodo(false);
      setNewTodo({ title: "", description: "", due_date: "", priority: "medium" });
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to add task");
    }
  };

  // Save targets (Admin only)
  const handleSaveTargets = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/sales/dashboard/targets`, targets, { headers });
      toast.success("Targets updated!");
      setShowSetTargets(false);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to save targets");
    }
  };

  // Open targets modal with current values
  const openTargetsModal = () => {
    const currentTargets = dashboardData?.performance?.targets || {};
    setTargets({
      instructions: currentTargets.instructions || 0,
      sales_agreed: currentTargets.sales_agreed || 0,
      exchanges: currentTargets.exchanges || 0,
      completions: currentTargets.completions || 0
    });
    setShowSetTargets(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short"
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "£0";
    return `£${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const { action_items = [], diary = {}, performance = {}, feeds = {}, pending = {} } = dashboardData || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Good Morning, {user?.name?.split(' ')[0] || "Team"}
            </h1>
            <p className="text-slate-500">
              {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboardData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/progression')} data-testid="sales-progression-btn">
              <TrendingUp className="w-4 h-4 mr-2" />
              Sales Progression
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAddApplicant(true)} data-testid="add-applicant-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Applicant
            </Button>
            {/* SI-P0-04: Redirect to Sales Instructions module */}
            <Button size="sm" onClick={() => navigate('/sales/instructions/list')} data-testid="new-instruction-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Instruction
            </Button>
          </div>
        </div>

        {/* Global Search Bar */}
        <div className="relative mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search applicants, properties, vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base bg-white shadow-sm"
              data-testid="global-search-input"
            />
            {searching && (
              <Loader2 className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-slate-400" />
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {searchResults && searchResults.totals?.total > 0 && (
            <Card className="absolute z-50 w-full mt-2 shadow-lg max-h-96 overflow-auto">
              <CardContent className="p-3">
                {searchResults.clients?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Clients ({searchResults.clients.length})</p>
                    {searchResults.clients.map((client, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                        onClick={() => {
                          navigate(client.link);
                          setSearchResults(null);
                          setSearchQuery("");
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{client.name}</p>
                            <p className="text-xs text-slate-500">{client.email || client.phone}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">{client.type?.replace("_", " ")}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {searchResults.sales_properties?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Properties ({searchResults.sales_properties.length})</p>
                    {searchResults.sales_properties.map((prop, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                        onClick={() => {
                          navigate(prop.link);
                          setSearchResults(null);
                          setSearchQuery("");
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Home className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{prop.address}</p>
                            <p className="text-xs text-slate-500">{prop.postcode} • {prop.status}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600">{formatCurrency(prop.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-12 gap-6">
          
          {/* Left Column - Action Items & To-Do */}
          <div className="col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    Action Items
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowAddTodo(true)} data-testid="add-todo-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[500px] overflow-auto">
                {action_items.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">All caught up!</p>
                    <p className="text-xs text-slate-400">No pending actions</p>
                  </div>
                ) : (
                  action_items.map((item, idx) => {
                    const PriorityIcon = priorityIcons[item.priority] || Clock;
                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${priorityColors[item.priority] || priorityColors.medium} cursor-pointer hover:shadow-sm transition-shadow`}
                        onClick={() => item.link && navigate(item.link)}
                        data-testid={`action-item-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          {item.type === "todo" && (
                            <Checkbox
                              className="mt-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCompleteTodo(item.id);
                              }}
                              data-testid={`complete-todo-${idx}`}
                            />
                          )}
                          {item.type !== "todo" && (
                            <PriorityIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            {item.subtitle && (
                              <p className="text-xs opacity-75 truncate">{item.subtitle}</p>
                            )}
                            {item.due_date && (
                              <p className="text-xs mt-1 opacity-75">Due: {formatDate(item.due_date)}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs flex-shrink-0 capitalize">
                            {item.type}
                          </Badge>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle Column - Diary */}
          <div className="col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Today's Diary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={diaryTab} onValueChange={setDiaryTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="personal" data-testid="diary-personal-tab">My Diary</TabsTrigger>
                    <TabsTrigger value="office" data-testid="diary-office-tab">Office</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="personal" className="space-y-2 max-h-[400px] overflow-auto">
                    {diary.personal?.length === 0 ? (
                      <div className="text-center py-8">
                        <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">No appointments today</p>
                      </div>
                    ) : (
                      diary.personal?.map((appt, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`personal-appt-${idx}`}>
                          <div className="w-12 h-12 rounded-lg bg-purple-100 flex flex-col items-center justify-center">
                            <span className="text-xs font-semibold text-purple-600">
                              {formatTime(appt.start_time)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{appt.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {appt.client_name || appt.contact_name || appt.address || appt.location}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs capitalize">
                            {appt.appointment_type || appt.type}
                          </Badge>
                        </div>
                      ))
                    )}
                  </TabsContent>
                  
                  <TabsContent value="office" className="space-y-2 max-h-[400px] overflow-auto">
                    {diary.office?.length === 0 ? (
                      <div className="text-center py-8">
                        <Building className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">No office appointments today</p>
                      </div>
                    ) : (
                      diary.office?.map((appt, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`office-appt-${idx}`}>
                          <div className="w-12 h-12 rounded-lg bg-slate-200 flex flex-col items-center justify-center">
                            <span className="text-xs font-semibold text-slate-600">
                              {formatTime(appt.start_time)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{appt.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {appt.client_name || appt.contact_name || appt.address || appt.location}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs capitalize">
                            {appt.appointment_type || appt.type}
                          </Badge>
                        </div>
                      ))
                    )}
                  </TabsContent>
                </Tabs>
                
                <Button 
                  variant="outline" 
                  className="w-full mt-4" 
                  onClick={() => navigate('/diary')}
                  data-testid="view-full-diary-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  View Full Diary
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Performance Stats */}
          <div className="col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-600" />
                    {performance.month} Performance
                  </CardTitle>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={openTargetsModal} data-testid="set-targets-btn">
                      <Target className="w-4 h-4 mr-1" />
                      Set Targets
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="personal">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="personal" data-testid="performance-personal-tab">My Stats</TabsTrigger>
                    <TabsTrigger value="office" data-testid="performance-office-tab">Office</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="personal" className="space-y-3">
                    {/* Stats with Targets */}
                    <div className="space-y-3">
                      {/* Instructions */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-blue-600">Instructions</span>
                          <span className="text-sm font-bold text-blue-700">
                            {performance.personal?.instructions || 0}
                            {performance.targets?.instructions > 0 && (
                              <span className="text-xs font-normal text-blue-500"> / {performance.targets.instructions}</span>
                            )}
                          </span>
                        </div>
                        {performance.targets?.instructions > 0 && (
                          <div className="w-full bg-blue-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all" 
                              style={{ width: `${Math.min(100, ((performance.personal?.instructions || 0) / performance.targets.instructions) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Sales Agreed */}
                      <div className="bg-purple-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-purple-600">Sales Agreed</span>
                          <span className="text-sm font-bold text-purple-700">
                            {performance.personal?.sales_agreed || 0}
                            {performance.targets?.sales_agreed > 0 && (
                              <span className="text-xs font-normal text-purple-500"> / {performance.targets.sales_agreed}</span>
                            )}
                          </span>
                        </div>
                        {performance.targets?.sales_agreed > 0 && (
                          <div className="w-full bg-purple-200 rounded-full h-2">
                            <div 
                              className="bg-purple-600 h-2 rounded-full transition-all" 
                              style={{ width: `${Math.min(100, ((performance.personal?.sales_agreed || 0) / performance.targets.sales_agreed) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Exchanges */}
                      <div className="bg-amber-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-amber-600">Exchanges</span>
                          <span className="text-sm font-bold text-amber-700">
                            {performance.personal?.exchanges || 0}
                            {performance.targets?.exchanges > 0 && (
                              <span className="text-xs font-normal text-amber-500"> / {performance.targets.exchanges}</span>
                            )}
                          </span>
                        </div>
                        {performance.targets?.exchanges > 0 && (
                          <div className="w-full bg-amber-200 rounded-full h-2">
                            <div 
                              className="bg-amber-600 h-2 rounded-full transition-all" 
                              style={{ width: `${Math.min(100, ((performance.personal?.exchanges || 0) / performance.targets.exchanges) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Completions */}
                      <div className="bg-emerald-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-emerald-600">Completions</span>
                          <span className="text-sm font-bold text-emerald-700">
                            {performance.personal?.completions || 0}
                            {performance.targets?.completions > 0 && (
                              <span className="text-xs font-normal text-emerald-500"> / {performance.targets.completions}</span>
                            )}
                          </span>
                        </div>
                        {performance.targets?.completions > 0 && (
                          <div className="w-full bg-emerald-200 rounded-full h-2">
                            <div 
                              className="bg-emerald-600 h-2 rounded-full transition-all" 
                              style={{ width: `${Math.min(100, ((performance.personal?.completions || 0) / performance.targets.completions) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-400 mb-1">My Pipeline Value</p>
                      <p className="text-xl font-bold text-white">
                        {formatCurrency(performance.personal?.pipeline_value)}
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="office" className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-700">{performance.office?.instructions || 0}</p>
                        <p className="text-xs text-blue-600">Instructions</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-700">{performance.office?.sales_agreed || 0}</p>
                        <p className="text-xs text-purple-600">Sales Agreed</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-700">{performance.office?.exchanges || 0}</p>
                        <p className="text-xs text-amber-600">Exchanges</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-700">{performance.office?.completions || 0}</p>
                        <p className="text-xs text-emerald-600">Completions</p>
                      </div>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-4 text-center">
                      <p className="text-xs text-slate-400 mb-1">Office Pipeline Value</p>
                      <p className="text-2xl font-bold text-white">
                        {formatCurrency(performance.office?.pipeline_value)}
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Activity Feeds Row */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          {/* Recent Instructions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Recent Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feeds.recent_instructions?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No recent instructions</p>
              ) : (
                feeds.recent_instructions?.slice(0, 4).map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/sales/case/${item.case_id}`)}
                    data-testid={`recent-instruction-${idx}`}
                  >
                    <p className="text-sm font-medium truncate">{item.address_line1}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{item.postcode}</span>
                      <span className="text-xs font-semibold text-emerald-600">{formatCurrency(item.asking_price)}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Sales Agreed */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                Sales Agreed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feeds.recent_sales?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No recent sales</p>
              ) : (
                feeds.recent_sales?.slice(0, 4).map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/sales/case/${item.case_id}`)}
                    data-testid={`recent-sale-${idx}`}
                  >
                    <p className="text-sm font-medium truncate">{item.address_line1}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{item.buyer_name || item.postcode}</span>
                      <span className="text-xs font-semibold text-purple-600">{formatCurrency(item.offer_price)}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Exchanges */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-amber-600" />
                Exchanges
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feeds.recent_exchanges?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No recent exchanges</p>
              ) : (
                feeds.recent_exchanges?.slice(0, 4).map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/sales/case/${item.case_id}`)}
                    data-testid={`recent-exchange-${idx}`}
                  >
                    <p className="text-sm font-medium truncate">{item.address_line1}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{formatDate(item.exchanged_at)}</span>
                      <span className="text-xs font-semibold text-amber-600">{formatCurrency(item.offer_price)}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Completions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                Completions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feeds.recent_completions?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No recent completions</p>
              ) : (
                feeds.recent_completions?.slice(0, 4).map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/sales/case/${item.case_id}`)}
                    data-testid={`recent-completion-${idx}`}
                  >
                    <p className="text-sm font-medium truncate">{item.address_line1}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{formatDate(item.completed_at)}</span>
                      <span className="text-xs font-semibold text-emerald-600">{formatCurrency(item.offer_price)}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Items Row */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          {/* Pending Viewings */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  Pending Viewings
                </CardTitle>
                <Badge variant="outline">{pending.viewings?.length || 0}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-48 overflow-auto">
              {pending.viewings?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No pending viewings</p>
              ) : (
                pending.viewings?.map((v, idx) => (
                  <div key={idx} className="p-2 bg-slate-50 rounded-lg" data-testid={`pending-viewing-${idx}`}>
                    <p className="text-sm font-medium truncate">{v.property_address || v.address}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{v.buyer_name || v.contact_name}</span>
                      <Badge variant="outline" className="text-xs capitalize">{v.status}</Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Pending Valuations */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-purple-600" />
                  Pending Valuations
                </CardTitle>
                <Badge variant="outline">{pending.valuations?.length || 0}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-48 overflow-auto">
              {pending.valuations?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No pending valuations</p>
              ) : (
                pending.valuations?.map((v, idx) => (
                  <div 
                    key={idx} 
                    className="p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => v.case_id && navigate(`/sales/case/${v.case_id}`)}
                    data-testid={`pending-valuation-${idx}`}
                  >
                    <p className="text-sm font-medium truncate">{v.address || v.address_line1}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{v.contact_name || v.vendor_name}</span>
                      <span className="text-xs text-slate-400">{formatDate(v.valuation_date)}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Outstanding Feedback */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-amber-600" />
                  Outstanding Feedback
                </CardTitle>
                <Badge variant="outline">{pending.feedback?.length || 0}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-48 overflow-auto">
              {pending.feedback?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No outstanding feedback</p>
              ) : (
                pending.feedback?.map((f, idx) => (
                  <div key={idx} className="p-2 bg-amber-50 rounded-lg" data-testid={`outstanding-feedback-${idx}`}>
                    <p className="text-sm font-medium truncate">{f.property_address || f.address}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{f.buyer_name || f.contact_name}</span>
                      <Badge className="text-xs bg-amber-100 text-amber-700">Needs Feedback</Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* SI-P0-04: Removed New Instruction Modal - now redirects to /sales/instructions/list */}

      {/* Add Applicant Modal */}
      <AddApplicantForm
        open={showAddApplicant}
        onOpenChange={setShowAddApplicant}
        onSuccess={() => {
          setShowAddApplicant(false);
          fetchDashboardData();
          toast.success("Applicant added successfully");
        }}
      />

      {/* Add To-Do Modal */}
      <Dialog open={showAddTodo} onOpenChange={setShowAddTodo}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>Create a new to-do item for your action list</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title *</Label>
              <Input
                value={newTodo.title}
                onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
                placeholder="e.g., Call back John Smith"
                className="mt-1"
                data-testid="todo-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newTodo.description}
                onChange={(e) => setNewTodo({ ...newTodo, description: e.target.value })}
                placeholder="Additional details..."
                className="mt-1"
                rows={3}
                data-testid="todo-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newTodo.due_date}
                  onChange={(e) => setNewTodo({ ...newTodo, due_date: e.target.value })}
                  className="mt-1"
                  data-testid="todo-due-date-input"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={newTodo.priority}
                  onValueChange={(v) => setNewTodo({ ...newTodo, priority: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="todo-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTodo(false)}>Cancel</Button>
            <Button onClick={handleAddTodo} data-testid="save-todo-btn">Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Monthly Targets Modal (Admin Only) */}
      <Dialog open={showSetTargets} onOpenChange={setShowSetTargets}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Monthly Targets</DialogTitle>
            <DialogDescription>Set your performance targets for {performance.month}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Instructions Target</Label>
              <Input
                type="number"
                min="0"
                value={targets.instructions}
                onChange={(e) => setTargets({ ...targets, instructions: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="target-instructions-input"
              />
            </div>
            <div>
              <Label>Sales Agreed Target</Label>
              <Input
                type="number"
                min="0"
                value={targets.sales_agreed}
                onChange={(e) => setTargets({ ...targets, sales_agreed: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="target-sales-input"
              />
            </div>
            <div>
              <Label>Exchanges Target</Label>
              <Input
                type="number"
                min="0"
                value={targets.exchanges}
                onChange={(e) => setTargets({ ...targets, exchanges: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="target-exchanges-input"
              />
            </div>
            <div>
              <Label>Completions Target</Label>
              <Input
                type="number"
                min="0"
                value={targets.completions}
                onChange={(e) => setTargets({ ...targets, completions: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="target-completions-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetTargets(false)}>Cancel</Button>
            <Button onClick={handleSaveTargets} data-testid="save-targets-btn">Save Targets</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesDashboard;
